import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useKeenSlider } from 'keen-slider/react';
import { Builder } from '@builder.io/react';

// Autoplay plugin for keen-slider
function AutoplayPlugin(slider) {
  let timeout;
  let mouseOver = false;

  function clearNextTimeout() {
    clearTimeout(timeout);
  }

  function nextTimeout() {
    clearTimeout(timeout);
    if (mouseOver) return;
    timeout = setTimeout(() => {
      slider.next();
    }, 2500); // 2.5 seconds per slide
  }

  slider.on("created", () => {
    slider.container.addEventListener("mouseover", () => {
      mouseOver = true;
      clearNextTimeout();
    });
    slider.container.addEventListener("mouseout", () => {
      mouseOver = false;
      nextTimeout();
    });
    nextTimeout();
  });
  slider.on("dragStarted", clearNextTimeout);
  slider.on("animationEnded", nextTimeout);
  slider.on("updated", nextTimeout);
}

export const CarouselAutoFrames = ({
  items = [],
  loop = true,
  spacing = 20,
  perView = { desktop: 5, tablet: 3, mobile: 1 },
  imageMaxHeight = 300,
}) => {
  const [currentImpactSlide, setCurrentImpactSlide] = useState(0);
  const [loadedImpact, setLoadedImpact] = useState(false);
  const { i18n } = useTranslation();
  const lang = i18n.language;

  // Responsive perView
  const getPerView = () => {
    if (window.innerWidth < 640) return perView.mobile || 1;
    if (window.innerWidth < 1024) return perView.tablet || 3;
    return perView.desktop || 5;
  };
  const [perViewValue, setPerViewValue] = useState(getPerView());

  // Update perView on resize
  useState(() => {
    const handleResize = () => setPerViewValue(getPerView());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Only use slider if there are more items than perViewValue
  const useSlider = items.length > perViewValue;
  const displayItems = useSlider ? items : items;

  const [sliderRefImpactSlider, instanceRefImpactSlider] = useKeenSlider(
    {
      mode: 'snap',
      slideChanged(slider) {
        setCurrentImpactSlide(slider.track.details.rel);
      },
      created() {
        setLoadedImpact(true);
      },
      loop,
      slides: {
        perView: perViewValue,
        spacing,
      },
    },
    useSlider ? [AutoplayPlugin] : []
  );

  return (
    <section className={`flex flex-col`}>
      <div className="relative">
        {useSlider ? (
          <>
            <div ref={sliderRefImpactSlider} className="keen-slider flex justify-between items-center">
              {items.map(({ img, link, openInNewTab }, index) => (
                <div className="keen-slider__slide flex w-full basis-36 flex-col items-center gap-1" key={index}>
                  {link ? (
                    <a href={link} target={openInNewTab ? '_blank' : '_self'} rel={openInNewTab ? 'noopener noreferrer' : undefined}>
                      <img src={img.src} alt={img.alt} style={{ maxHeight: imageMaxHeight + 'px', width: 'auto' }} />
                    </a>
                  ) : (
                    <img src={img.src} alt={img.alt} style={{ maxHeight: imageMaxHeight + 'px', width: 'auto' }} />
                  )}
                </div>
              ))}
            </div>
            {loadedImpact && instanceRefImpactSlider.current && (
              <div className="mt-4 overflow-x-auto custom-carousel-scroll">
                <div className="flex justify-start sm:justify-center gap-2 px-2 min-w-max">
                  {items.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => instanceRefImpactSlider.current?.moveToIdx(idx)}
                      className={`min-w-[12px] min-h-[12px] w-3 h-3 flex-shrink-0 rounded-full border-2 border-gray-400 focus:outline-none transition-all duration-200 ${currentImpactSlide === idx ? 'bg-[#253B7E] border-[#253B7E]' : 'bg-white'
                        }`}
                      aria-label={`Go to slide ${idx + 1}`}
                    />
                  ))}
                </div>
              </div>
            )}


          </>
        ) : (
          <div className="flex justify-center items-center gap-8">
            {items.map(({ img, link, openInNewTab }, index) => (
              <div className="flex flex-col items-center" key={index}>
                {link ? (
                  <a href={link} target={openInNewTab ? '_blank' : '_self'} rel={openInNewTab ? 'noopener noreferrer' : undefined}>
                    <img src={img.src} alt={img.alt} style={{ maxHeight: imageMaxHeight + 'px', width: 'auto' }} />
                  </a>
                ) : (
                  <img src={img.src} alt={img.alt} style={{ maxHeight: imageMaxHeight + 'px', width: 'auto' }} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};


Builder.registerComponent(CarouselAutoFrames, {
  name: 'CarouselAutoFrames',
  inputs: [
    {
      name: 'items',
      type: 'list',
      subFields: [
        {
          name: 'img',
          type: 'object',
          subFields: [
            { name: 'src', type: 'file', allowedFileTypes: ['jpeg', 'png', 'svg'] },
            { name: 'alt', type: 'string' },
          ],
          required: true,
        },
        { name: 'link', type: 'string', helperText: 'Optional URL to link to when image is clicked.' },
        { name: 'openInNewTab', type: 'boolean', defaultValue: false, helperText: 'Open link in new tab?' },
      ],
    },
    { name: 'loop', type: 'boolean', defaultValue: true },
    { name: 'spacing', type: 'number', defaultValue: 20 },
    {
      name: 'perView',
      type: 'object',
      subFields: [
        { name: 'desktop', type: 'number', defaultValue: 5 },
        { name: 'tablet', type: 'number', defaultValue: 3 },
        { name: 'mobile', type: 'number', defaultValue: 1 },
      ],
      defaultValue: { desktop: 5, tablet: 3, mobile: 1 },
    },
    { name: 'imageMaxHeight', type: 'number', defaultValue: 300 },
  ],
});
